import { useEffect } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

interface ConflictDialogProps {
  open: boolean;
  loading?: boolean;
  onReload: () => void; // discard local edits, take the server copy
  onOverwrite: () => void; // keep local edits, force-save over the server copy
  onDismiss: () => void; // close WITHOUT resolving — local edits are preserved
}

/**
 * Save-conflict (409) prompt with THREE outcomes. Unlike a generic two-button
 * confirm dialog, dismissing this (backdrop click, Escape, or the close button)
 * is a NO-OP that keeps the user's unsaved edits — only the explicit "Reload"
 * (discard) and "Overwrite" (force-save) buttons mutate state. This prevents an
 * accidental backdrop click from silently discarding work.
 */
export function ConflictDialog({
  open,
  loading = false,
  onReload,
  onOverwrite,
  onDismiss,
}: ConflictDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onDismiss} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-dialog-title"
        className="relative z-10 mx-4 w-full max-w-md"
      >
        <div className="card border-2 border-amber-500/30">
          <div className="mb-4 flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
              <AlertTriangle size={20} className="text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 id="conflict-dialog-title" className="text-base font-semibold text-white">
                Dashboard changed elsewhere
              </h3>
              <p className="mt-1 text-sm text-gray-400">
                This dashboard was updated since you opened it. Reload to discard your changes,
                Overwrite to keep yours, or dismiss to keep editing.
              </p>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              disabled={loading}
              className="shrink-0 p-1 text-gray-500 transition-colors hover:text-gray-300"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onReload}
              disabled={loading}
              className="btn-secondary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={onOverwrite}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Overwrite
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
